import i18next from 'i18next'
import {inject, injectable} from 'inversify'
import {action, computed, makeObservable, observable, runInAction} from 'mobx'
import {LoadingState} from '@/types'
import {
    CreatePaymentLinkViewType,
    createPaymentLink,
    PaymentLink,
    TerminalSettingsResponse
} from '@/api'
import {openErrorNotification, openSuccessNotification} from '@/utils'
import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'
import {
    OnlinePaymentsStoreSymbol,
    TOnlinePaymentsStore,
    OnlinePaymentsTableFilterRangePickerContainerStoreSymbol
} from '@/pages/OnlinePayments'
import {
    OnlinePaymentsFilterButtonStoreSymbol,
    IOnlinePaymentsFilterButtonStore
} from '~/code/pages/OnlinePayments/components/OnlinePaymentsFilterButton'
import {
    PayByLinkLinkDetailsModalContainerStoreSymbol,
    PayByLinkNewPaymentLinkModalContainerStoreSymbol,
    PayByLinkPaymentLinkDetailsStoreSymbol
} from '@/pages/PayByLink'
import {TModalContainerStore} from '@/components/containers/ModalContainer/TModalContainerStore'
import {
    INewPaymentLinkModalStore,
    TPaymentLinkDetailsModalStore
} from '@/pages/PayByLink/components'
import {
    AvailableStoresDataStore,
    AvailableStoresDataStoreSymbol
} from '@/stores/store-and-terminals'
import {
    formatCreatePayLinkRequest,
    getEcomTerminalsSelectType
} from '@/stores/pay-by-link/services/utils'
import {MerchantTerminalSelectType} from '@/stores/pay-by-link/models/MerchantTerminalSelectType'
import {TRangePickerContainerStore} from '@/components/containers/RangePickerContainer/TRangePickerContainerStore'

@injectable()
export class NewPaymentLinkModalStore implements INewPaymentLinkModalStore {
    private readonly _onlinePaymentsStore: TOnlinePaymentsStore
    private readonly _onlinePaymentsFilterButtonStore: IOnlinePaymentsFilterButtonStore
    private readonly _linkDetailsModalStore: TModalContainerStore
    private readonly _newPaymentLinkModalStore: TModalContainerStore
    private readonly _merchantStoresStore: AvailableStoresDataStore
    private readonly _paymentLinkDetailsModalStore: TPaymentLinkDetailsModalStore
    private readonly _onlinePaymentsRangePickerStore: TRangePickerContainerStore

    loadingState = LoadingState.IDLE
    paymentLink: PaymentLink = {} as PaymentLink

    get terminals(): MerchantTerminalSelectType[] {
        return getEcomTerminalsSelectType(this._merchantStoresStore.ecomStores)
    }

    get terminalSettings(): TerminalSettingsResponse {
        return this._paymentLinkDetailsModalStore?.terminalSettings
    }

    constructor(
        @inject(PayByLinkLinkDetailsModalContainerStoreSymbol)
        linkDetailsModalStore: TModalContainerStore,
        @inject(PayByLinkNewPaymentLinkModalContainerStoreSymbol)
        newPaymentLinkModalStore: TModalContainerStore,
        @inject(AvailableStoresDataStoreSymbol) merchantStoresStore: AvailableStoresDataStore,
        @inject(PayByLinkPaymentLinkDetailsStoreSymbol)
        paymentLinkDetailsModalStore: TPaymentLinkDetailsModalStore,
        @inject(OnlinePaymentsStoreSymbol) onlinePaymentsStore: TOnlinePaymentsStore,
        @inject(OnlinePaymentsFilterButtonStoreSymbol)
        onlinePaymentsFilterButtonStore: IOnlinePaymentsFilterButtonStore,
        @inject(OnlinePaymentsTableFilterRangePickerContainerStoreSymbol)
        onlinePaymentsRangePickerStore: TRangePickerContainerStore
    ) {
        this._linkDetailsModalStore = linkDetailsModalStore
        this._newPaymentLinkModalStore = newPaymentLinkModalStore
        this._merchantStoresStore = merchantStoresStore
        this._paymentLinkDetailsModalStore = paymentLinkDetailsModalStore
        this._onlinePaymentsStore = onlinePaymentsStore
        this._onlinePaymentsFilterButtonStore = onlinePaymentsFilterButtonStore
        this._onlinePaymentsRangePickerStore = onlinePaymentsRangePickerStore

        makeObservable(this, {
            loadingState: observable,
            paymentLink: observable,
            terminals: computed,
            terminalSettings: computed,
            createNewPaymentLink: action.bound,
            createSimilarPaymentLink: action.bound,
            setPaymentLink: action.bound,
            closeModals: action.bound,
            reset: action.bound
        })
    }

    public setPaymentLink(paymentLink: PaymentLink) {
        this.paymentLink = paymentLink
    }

    public createSimilarPaymentLink(similarPaymentLink: PaymentLink) {
        this.setPaymentLink(similarPaymentLink)
        this._linkDetailsModalStore.setOpen(false)
        this._newPaymentLinkModalStore.setOpen(true)
    }

    public reset() {
        this.setPaymentLink(null)
    }

    public closeModals() {
        this._newPaymentLinkModalStore.setOpen(false)
        this._linkDetailsModalStore.setOpen(false)
    }

    onCreateSuccess = (result) => {
        this.loadingState = LoadingState.DONE
        this._paymentLinkDetailsModalStore.widgetType = 'linkDetails'
        this._paymentLinkDetailsModalStore.isNewLinkDetails = true
        this._paymentLinkDetailsModalStore.setPaymentLink(result)
        this._newPaymentLinkModalStore.setOpen(false)
        this._linkDetailsModalStore.setOpen(true)
    }

    viewAllPayments = async (paymentLink: PaymentLink) => {
        this.closeModals()

        await this._onlinePaymentsStore.openByMultiUsePaymentLink(paymentLink)
    }

    public async createNewPaymentLink(values: CreatePaymentLinkViewType) {
        const defaultTerminalId =
            this._merchantStoresStore.ecomStores &&
            this._merchantStoresStore.ecomStores[0]?.terminals &&
            this._merchantStoresStore.ecomStores[0]?.terminals.find(
                (terminal) => terminal.isActive && terminal.isDefault
            )?.id

        const activeTerminalId =
            this._merchantStoresStore.ecomStores &&
            this._merchantStoresStore.ecomStores[0]?.terminals &&
            this._merchantStoresStore.ecomStores[0]?.terminals.find((terminal) => terminal.isActive)
                ?.id

        const terminalId = defaultTerminalId ? defaultTerminalId : activeTerminalId

        const formattedValues = formatCreatePayLinkRequest(values, terminalId)

        this.loadingState = LoadingState.LOADING

        const isFirefox = navigator.userAgent.indexOf('Firefox') != -1

        if (isFirefox) {
            try {
                const {result, error} = await createPaymentLink(formattedValues)

                runInAction(() => {
                    if (error) {
                        this.loadingState = LoadingState.FAILED
                        return openErrorNotification(error.message)
                    }

                    this.onCreateSuccess(result)
                    navigator.clipboard.writeText(result.url)

                    return openSuccessNotification(
                        i18next.t(
                            'NewPaymentLinkModal.labels.theNewPaymentLinkIsCreatedAndCopiedToClipboard'
                        )
                    )
                })
            } catch (error) {
                captureSentryError({
                    name: 'PaymentLink Create Error',
                    description: getErrorMessage(error, 'Failed to create payment link'),
                    fingerprint: ['payment-link-create-firefox'],
                    extra: {browser: 'firefox'},
                    error
                })
                this.loadingState = LoadingState.FAILED
                return openErrorNotification(i18next.t('globals:errors.general'))
            }
        } else {
            await runInAction(async () => {
                const text = new ClipboardItem({
                    'text/plain': createPaymentLink(formattedValues)
                        .then(({result, error}) => {
                            if (error) {
                                this.loadingState = LoadingState.FAILED
                                throw new Error(error.message)
                            }

                            this.onCreateSuccess(result)

                            return new Blob([result.url], {type: 'text/plain'})
                        })
                        .catch((error) => {
                            captureSentryError({
                                name: 'PaymentLink Create Error',
                                description: getErrorMessage(
                                    error,
                                    'Failed to create payment link'
                                ),
                                fingerprint: ['payment-link-create-other'],
                                extra: {browser: 'other'},
                                error
                            })
                            this.loadingState = LoadingState.FAILED
                            openErrorNotification(i18next.t('globals:errors.general'))
                            throw new Error(error.message)
                        })
                })

                await navigator.clipboard.write([text])

                return openSuccessNotification(
                    i18next.t(
                        'NewPaymentLinkModal.labels.theNewPaymentLinkIsCreatedAndCopiedToClipboard'
                    )
                )
            })
        }
    }
}
